// ============================================
// useNotifications — SSE + REST
// ============================================
import { useState, useEffect, useCallback, useRef } from 'react';
const API_URL = import.meta.env.VITE_API_URL || '';
function getToken() {
    return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
export function useNotifications() {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const abortRef = useRef(null);
    // ── Fetch initial list ───────────────────────────────────────────────────────
    const fetchNotifications = useCallback(async () => {
        const token = getToken();
        if (!token)
            return;
        try {
            const res = await fetch(`${API_URL}/api/notifications`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok)
                return;
            const json = await res.json();
            const data = json.data;
            setNotifications(data.notifications ?? []);
            setUnreadCount(data.unreadCount ?? 0);
        }
        catch {
            // ignore
        }
    }, []);
    // ── SSE stream ───────────────────────────────────────────────────────────────
    const connectSse = useCallback(() => {
        const token = getToken();
        if (!token)
            return;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        (async () => {
            try {
                const res = await fetch(`${API_URL}/api/notifications/stream`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (!res.ok || !res.body)
                    return;
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let currentEvent = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        // Connexion fermée proprement (restart service, timeout réseau…) → reconnexion
                        if (!controller.signal.aborted)
                            setTimeout(() => connectSse(), 3000);
                        break;
                    }
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() ?? '';
                    for (const line of lines) {
                        if (line.startsWith('event:')) {
                            currentEvent = line.slice(6).trim();
                        }
                        else if (line === '') {
                            currentEvent = '';
                        }
                        else if (line.startsWith('data:')) {
                            // Ignorer tout event qui n'est pas "notification"
                            if (currentEvent && currentEvent !== 'notification')
                                continue;
                            const raw = line.slice(5).trim();
                            if (!raw)
                                continue;
                            try {
                                const notif = JSON.parse(raw);
                                if (!notif.id)
                                    continue; // ignorer les events sans id valide
                                // Ne pas dupliquer
                                setNotifications(prev => {
                                    if (prev.find(n => n.id === notif.id))
                                        return prev;
                                    return [notif, ...prev];
                                });
                                if (!notif.read) {
                                    setUnreadCount(c => c + 1);
                                }
                            }
                            catch {
                                // JSON invalide — ignorer
                            }
                        }
                    }
                }
            }
            catch (err) {
                if (err instanceof Error && err.name === 'AbortError')
                    return;
                // Reconnexion automatique après 3s
                setTimeout(() => connectSse(), 3000);
            }
        })();
    }, []);
    useEffect(() => {
        fetchNotifications();
        connectSse();
        return () => abortRef.current?.abort();
    }, [fetchNotifications, connectSse]);
    // ── Actions ──────────────────────────────────────────────────────────────────
    const markRead = useCallback(async (id) => {
        const token = getToken();
        try {
            await fetch(`${API_URL}/api/notifications/${id}/read`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` },
            });
            setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
            setUnreadCount(c => Math.max(0, c - 1));
        }
        catch {
            // ignore
        }
    }, []);
    const markAllRead = useCallback(async () => {
        const token = getToken();
        try {
            await fetch(`${API_URL}/api/notifications/read-all`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` },
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        }
        catch {
            // ignore
        }
    }, []);
    const clearRead = useCallback(async () => {
        const token = getToken();
        try {
            await fetch(`${API_URL}/api/notifications/read`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            setNotifications(prev => prev.filter(n => !n.read));
        }
        catch {
            // ignore
        }
    }, []);
    return { notifications, unreadCount, markRead, markAllRead, clearRead };
}
//# sourceMappingURL=useNotifications.js.map